import { Box, createStyles, Flex, Skeleton, Stack, Tabs } from '@mantine/core';
import { SettingsTitle } from '@repo/common/components';
import type { UserRole } from '@repo/common/enums/UserRole';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { Badge, Button, Icon, Text } from '@repo/foundations';
import { space } from '@repo/theme/primitives';
import { useMemo, type CSSProperties } from 'react';
import { useParams } from 'react-router';
import {
	useAuthUser,
	useCurrentUser,
	useSuspenseFeatureAccess,
} from '../../api';
import { useFeatureFlags } from '../../utils/featureFlags';
import { evaluateFeatureCondition } from '../../utils/featureFlags/complexFeatureUtils';
import {
	featureTabs,
	profileTabs,
	workspaceTabs,
	type TabType,
} from './settingsUtils';

const useStyles = createStyles(
	(theme, { isFullWidth }: { isFullWidth: boolean }) => ({
		settingsPanel: {
			maxWidth: isFullWidth ? '100%' : theme.other.space[160],
			paddingBottom: theme.spacing['5xl'],
			margin: '0 auto',
		},
	})
);

const isBeta = (tab: TabType) => {
	if (tab.isBeta && tab.betaExpiresAt) {
		return new Date(tab.betaExpiresAt) > new Date();
	}
	return false;
};

const isNew = (tab: TabType) => {
	if (tab.isNew && tab.newExpiresAt) {
		return new Date(tab.newExpiresAt) > new Date();
	}
	return false;
};

function TabGroupTitle(props: { title: string; style?: CSSProperties }) {
	const { title, style } = props;
	return (
		<Flex style={style ?? {}} align="center" gap="5px">
			<Text size="xs" weight="semibold" color="text/secondary/default">
				{title}
			</Text>
		</Flex>
	);
}

export default function SettingsPage() {
	const navigate = useNavigate();
	const { tab: browserPath, subtab } = useParams();
	const { workspace } = useAuthUser();
	const { user } = useAuthUser();
	const featureFlags = useFeatureFlags();
	const featureAccess = useSuspenseFeatureAccess();
	const { data: currentUser } = useCurrentUser();
	const userPermissions = currentUser?.permissions;
	const { settingsNav } = featureFlags;

	const filteredTabs = useMemo(() => {
		function filterTabs(tabs: TabType[]) {
			return tabs.filter((tab) => {
				// Check feature condition
				if (
					tab.featureCondition &&
					!evaluateFeatureCondition(
						tab.featureCondition,
						featureFlags,
						featureAccess
					)
				) {
					return false;
				}

				if (workspace.migrated_permission_v2) {
					if (tab.v2AllowedPermissions) {
						if (
							tab.v2DeniedPermissions &&
							tab.v2DeniedPermissions.some((permission) =>
								userPermissions?.includes(permission)
							)
						) {
							return false;
						}

						return tab.v2AllowedPermissions.some((permission) =>
							userPermissions?.includes(permission)
						);
					}
				} else {
					if (tab.allowedRoles) {
						return user
							? tab.allowedRoles.includes(user.role as UserRole)
							: false;
					}
				}

				return true;
			});
		}
		return {
			profileTabs: filterTabs(profileTabs(subtab)),
			workspaceTabs: filterTabs(workspaceTabs(workspace)),
			featureTabs: filterTabs(featureTabs()),
		};
	}, [featureFlags, featureAccess, user, userPermissions, workspace, subtab]);

	const tabs = [
		...filteredTabs.profileTabs,
		...filteredTabs.featureTabs,
		...filteredTabs.workspaceTabs,
	];
	const currentTab = browserPath
		? tabs.find((tab) => tab.id === browserPath)
		: filteredTabs.profileTabs[0];

	const isFullWidth = useMemo(
		() => currentTab?.id === 'audit-log' || currentTab?.id === 'members',
		[currentTab]
	);

	const { classes } = useStyles({ isFullWidth });

	if (!userPermissions) {
		return (
			<Box p="lg" style={{ display: 'flex' }}>
				{!settingsNav && (
					<Box mr={space[4]} w={space[60]}>
						{[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
							<Skeleton key={i} height={36} mb={8} />
						))}
					</Box>
				)}
				<Box sx={{ flex: 1 }}>
					<Skeleton height={40} width={200} mb={15} />
					<Skeleton height={20} width="80%" mb={35} />
					<Stack>
						{[1, 2, 3].map((i) => (
							<Skeleton key={i} height={50} />
						))}
					</Stack>
				</Box>
			</Box>
		);
	}

	if (!currentTab) {
		navigate('/403');
		return null;
	}

	const title = currentTab.title ?? currentTab.name;
	const TabElement = currentTab.component;

	return (
		<Box px="lg">
			{!settingsNav ? (
				<Tabs
					value={currentTab.id}
					onTabChange={(tabId) => {
						if (
							tabId === 'extensions' &&
							featureFlags.extensionsInIntegrationsPage
						) {
							navigate('/integrations/extensions');
						} else {
							navigate(`/settings/${tabId}`);
						}
					}}
					orientation="vertical"
					variant="pills"
					keepMounted={false}
				>
					<Tabs.List w={space[60]} style={{ gap: '1px' }} mr={space[6]}>
						<TabGroupTitle
							style={{
								padding: `${space[1]}px 0 ${space[2]}px ${space[1]}px`,
							}}
							title="Account"
						/>
						{filteredTabs.profileTabs.map((tab) => (
							<Tabs.Tab
								style={{ paddingLeft: space[1], paddingRight: space[1] }}
								key={tab.id}
								value={tab.id}
							>
								<Flex align="center" gap="5px">
									<Icon name={tab.iconName} />
									<Text size="sm" weight="semibold">
										{tab.sidebarTitle ?? tab.name}
									</Text>
									{isBeta(tab) && (
										<Badge variant="info" size="xs">
											Beta
										</Badge>
									)}
									{isNew(tab) && (
										<Badge variant="info" size="xs">
											New
										</Badge>
									)}
								</Flex>
							</Tabs.Tab>
						))}
						{filteredTabs.featureTabs.length > 0 && (
							<>
								<TabGroupTitle
									style={{
										marginTop: space[3],
										padding: `${space[1]}px 0 ${space[2]}px ${space[1]}px`,
									}}
									title="Features"
								/>
								{filteredTabs.featureTabs.map((tab) => (
									<Tabs.Tab
										style={{ paddingLeft: space[1], paddingRight: space[1] }}
										key={tab.id}
										id={tab.id}
										value={tab.id}
									>
										<Flex align="center" gap="5px">
											<Icon name={tab.iconName} />
											<Text size="sm" weight="semibold">
												{tab.sidebarTitle ?? tab.name}
											</Text>
											{isBeta(tab) && (
												<Badge variant="info" size="xs">
													Beta
												</Badge>
											)}
											{isNew(tab) && (
												<Badge variant="info" size="xs">
													New
												</Badge>
											)}
										</Flex>
									</Tabs.Tab>
								))}
							</>
						)}
						{filteredTabs.workspaceTabs.length > 0 && (
							<>
								<TabGroupTitle
									style={{
										marginTop: space[3],
										padding: `${space[1]}px 0 ${space[2]}px ${space[1]}px`,
									}}
									title="Administration"
								/>
								{filteredTabs.workspaceTabs.map((tab) => (
									<Tabs.Tab
										style={{ paddingLeft: space[1], paddingRight: space[1] }}
										key={tab.id}
										id={tab.id}
										value={tab.id}
									>
										<Flex align="center" gap="5px">
											<Icon name={tab.iconName} />
											<Text size="sm" weight="semibold">
												{tab.sidebarTitle ?? tab.name}
											</Text>
											{isBeta(tab) && (
												<Badge variant="info" size="xs">
													Beta
												</Badge>
											)}
											{isNew(tab) && (
												<Badge variant="info" size="xs">
													New
												</Badge>
											)}
										</Flex>
									</Tabs.Tab>
								))}
							</>
						)}
					</Tabs.List>
					{tabs.map((tab) => {
						const title = tab.title ?? tab.name;
						const TabElement = tab.component;

						// Template Tab also has a create template page that doesn't use the same headers as the other tabs
						if (tab.id === 'templates') {
							return (
								<Tabs.Panel
									key={tab.id}
									id={`panel-${tab.name}`}
									value={tab.id}
									className={classes.settingsPanel}
								>
									<TabElement />
								</Tabs.Panel>
							);
						}

						return (
							<Tabs.Panel
								key={tab.id}
								id={`panel-${tab.name}`}
								value={tab.id}
								className={classes.settingsPanel}
							>
								<Box>
									<Flex justify="space-between" align="center" mb="xl">
										<SettingsTitle>{title}</SettingsTitle>
										{tab.documentationLink && (
											<Button
												component="a"
												href={tab.documentationLink}
												target="_blank"
												variant="tertiary"
												size="sm"
												rightIconName="externalLink"
											>
												Documentation
											</Button>
										)}
									</Flex>
								</Box>
								<TabElement />
							</Tabs.Panel>
						);
					})}
				</Tabs>
			) : (
				<Box className={classes.settingsPanel}>
					<Box>
						<Flex justify="space-between" align="center" mb="xl">
							{currentTab.id !== 'templates' && (
								<SettingsTitle>{title}</SettingsTitle>
							)}
							{currentTab.id !== 'templates' &&
								currentTab.documentationLink && (
									<Button
										component="a"
										href={currentTab.documentationLink}
										target="_blank"
										variant="tertiary"
										size="sm"
										rightIconName="externalLink"
									>
										Documentation
									</Button>
								)}
						</Flex>
					</Box>
					<TabElement />
				</Box>
			)}
		</Box>
	);
}
